import { Link, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { ConceptGroupWorkflowDialog } from '@verantos-dev/feature-concept-groups-list';
import { projectPageKeys } from '@verantos-dev/feature-project-page';
import { type IsValidCreateUploadResponse } from '@verantos-dev/types-concepts';

interface ConceptGroupReviewActionProps {
  conceptGroupReviewData: {
    conceptGroupName: string;
    concepts: IsValidCreateUploadResponse[];
    filterKey?: string;
    previewId?: string;
    totalCount?: number;
    domainBreakdown?: Record<string, number>;
  };
  timestamp?: string;
  projectId?: string;
}

export const ConceptGroupReviewAction = ({
  conceptGroupReviewData,
  timestamp,
  projectId,
}: ConceptGroupReviewActionProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [showConceptGroupDialog, setShowConceptGroupDialog] = useState(false);

  // Memoize the dialog key to avoid recalculating on every render
  // Use conceptGroupName + timestamp to create a unique key
  // Timestamp is unique per message and avoids expensive hash computation on large concept arrays
  const dialogKey = useMemo(
    () => `${conceptGroupReviewData.conceptGroupName}-${timestamp || Date.now()}`,
    [conceptGroupReviewData.conceptGroupName, timestamp]
  );

  return (
    <>
      <Link
        component="button"
        variant="body2"
        onClick={() => setShowConceptGroupDialog(true)}
        sx={{
          mt: theme.spacing(4),
          textDecoration: 'underline',
          cursor: 'pointer',
          color: 'primary.main',
          textAlign: 'left',
          paddingInline: theme.spacing(3),
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          '&:hover': {
            color: 'primary.dark',
          },
        }}
      >
        <em>{conceptGroupReviewData.conceptGroupName}</em> concept group
      </Link>
      {showConceptGroupDialog && (
        <ConceptGroupWorkflowDialog
          key={dialogKey}
          onClose={() => setShowConceptGroupDialog(false)}
          initialData={conceptGroupReviewData}
          projectId={projectId}
          onSuccess={() => {
            if (projectId) {
              void queryClient.invalidateQueries({
                queryKey: projectPageKeys.artifactsForProject(projectId),
              });
              void queryClient.invalidateQueries({
                queryKey: projectPageKeys.activityForProject(projectId),
              });
            }
          }}
        />
      )}
    </>
  );
};
